// ═══════════════════════════════════════════════════════════════
// RevPlanPanel.jsx — XC-REV platform component (S7.B + S7.C)
// Path: src/components/RevPlanPanel.jsx
//
// S7.B + S7.C (May 21, 2026) — NEW FILE. Fourth platform component
//                               to ship after XC-DOC (S5), XC-MSG (S6),
//                               XC-TEAM (S7.A). Side panel — not a
//                               left-nav item per Taxonomy
//                               PLATFORM-COMPONENTS R12.
//
// ARCHITECTURE — Stub-only at S7
//   Two-tab side panel:
//     1. Chapters tab — 7 lifecycle-stage chapter placeholders
//                        (per Strategy §3d: Acquire / Develop / Operate /
//                         Maintain / Reposition / Realize / Hold)
//     2. Ledger tab — placeholder KPI grid + transactions table + recon card
//                      per sandbox v7.2 oprLedger reference
//
//   No live writes. No discrete-process gate-fire integration.
//   Real chapter content arrives at Tier 3 when discrete processes
//   start writing to RevPlan per their gate-fire descriptions in
//   processConfigs.js (S13+).
//
//   No /revPlanChapters, /revPlans, /ledger, /ledgerEntries, or
//   /transactions collections exist in production (greenfield —
//   confirmed by BATCH 2 root-collection check May 21, 2026).
//
// PROPS
//   workspace    — workspace object with workspaceId, label, type
//   isOpen       — bool: render the panel (default false)
//   onClose      — callback to close the panel
//   stage        — optional string: which stage's chapter to highlight
//                  ("acquire" / "develop" / "operate" etc.) When set,
//                  panel auto-scrolls to that chapter on open.
//                  Caller (Operate.jsx) passes "operate" by default.
//   mockMode     — if true, render with hardcoded sample data
//                  (for ?revplan-smoke=1 harness)
//
// FORWARD-COMPAT NOTES
//   - Chapter content rendered as placeholders. Real chapters write
//     to /revPlanChapters or similar at Tier 3 — schema TBD in
//     PA-XC-ENT or its own spec session.
//   - Ledger tab placeholder. Real ledger entries flow from Stripe
//     payouts (Run stage), QB sync (Maintain), and discrete process
//     financial gates (Acquire close, Realize sale).
//   - No /revPlanChapters or /ledger composite indexes needed at S7
//     (no live queries).
//
// AS REFERENCES
//   AS-S7-XC-ENT-SCOPE — workspace identity model still in flux;
//                         RevPlanPanel uses same workspaceDisplayName
//                         fallback pattern as Team.jsx
//                         (label → workspaceName → name → fallback)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';

// ── Chapter definitions — 7 lifecycle stages per Strategy §3d ────────────
// Each chapter maps to a lifecycle stage. Stage NAVs (in ShellNav) and
// chapter content scope are coupled — the Acquire chapter shows pro-forma
// + closing waterfalls, the Operate chapter shows NOI + occupancy + rent
// roll summary, etc. At Tier 3 each discrete process writes its outcome
// into the matching chapter (per processConfigs.js gate-fire descriptions).
const CHAPTERS = [
  {
    key: 'acquire',
    n: 0,
    title: 'Acquire',
    summary: 'Business case, target, pro forma, financing, closing.',
    arrivesIn: 'S13–S15 (Scoutstack + Closestack discrete processes)',
  },
  {
    key: 'develop',
    n: 1,
    title: 'Develop',
    summary: 'Project scope, budget, draws, bid package, completion.',
    arrivesIn: 'S16 (Buildstack discrete processes)',
  },
  {
    key: 'operate',
    n: 2,
    title: 'Operate',
    summary: 'STR/LTR revenue, occupancy, leases, NOI, rent roll.',
    arrivesIn: 'S17–S19 (Launchstack + Runstack discrete processes)',
  },
  {
    key: 'maintain',
    n: 3,
    title: 'Maintain',
    summary: 'Work orders, vendor spend, capex, reserves.',
    arrivesIn: 'S13 (workOrders) + S20 (Maintainstack discrete processes)',
  },
  {
    key: 'reposition',
    n: 4,
    title: 'Reposition',
    summary: 'Refinance, recapitalization, repositioning capex.',
    arrivesIn: 'S21 (Repositionstack discrete processes)',
  },
  {
    key: 'realize',
    n: 5,
    title: 'Realize',
    summary: 'Exit strategy, sale, distribution waterfall, K-1.',
    arrivesIn: 'S22 (Realizestack discrete processes)',
  },
  {
    key: 'hold',
    n: 6,
    title: 'Hold',
    summary: 'Idle / held asset state — passive tracking.',
    arrivesIn: 'S23 (Holdstack discrete processes)',
  },
];

// ── Inline styles ────────────────────────────────────────────────────────
const styles = {
  // Backdrop — covers screen behind panel
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    transition: 'opacity 200ms',
  },
  // Side panel — slides in from right
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '480px',
    maxWidth: '90vw',
    backgroundColor: '#FFFFFF',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    overflow: 'hidden',
  },
  // Header
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#F8F9FA',
    flexShrink: 0,
  },
  headerTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  badge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    color: '#003060',
    backgroundColor: '#DBEAFE',
    padding: '3px 8px',
    borderRadius: 4,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 12,
    color: '#6B7280',
    margin: 0,
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: 22,
    cursor: 'pointer',
    color: '#9CA3AF',
    padding: 0,
    width: 32,
    height: 32,
    flexShrink: 0,
    fontFamily: 'inherit',
  },
  // Tab bar
  tabBar: {
    display: 'flex',
    gap: 4,
    borderBottom: '1px solid #E5E7EB',
    paddingLeft: 24,
    flexShrink: 0,
  },
  tab: {
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#6B7280',
    borderBottom: '2px solid transparent',
    fontFamily: 'inherit',
    marginBottom: -1,
  },
  tabActive: {
    color: '#003060',
    borderBottom: '2px solid #00B8D0',
  },
  // Body — scrollable area
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
  },
  // Chapters tab
  chapterCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  chapterCardActive: {
    borderColor: '#00B8D0',
    backgroundColor: '#F0FBFD',
  },
  chapterHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 6,
    gap: 8,
  },
  chapterNumber: {
    fontSize: 11,
    fontWeight: 700,
    color: '#9CA3AF',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  chapterTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
  },
  chapterSummary: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.5,
    margin: '4px 0 8px',
  },
  chapterPlaceholder: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#9CA3AF',
    padding: '8px 12px',
    backgroundColor: '#FAFBFC',
    borderLeft: '3px solid #E5E7EB',
    borderRadius: 2,
  },
  // Ledger tab
  ledgerKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
    marginBottom: 20,
  },
  ledgerKpiTile: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 12,
  },
  ledgerKpiLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  ledgerKpiValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#003060',
  },
  ledgerKpiSub: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 2,
  },
  ledgerCardHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  ledgerCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  ledgerEmptyState: {
    fontSize: 13,
    color: '#6B7280',
    fontStyle: 'italic',
    padding: '24px 12px',
    textAlign: 'center',
    backgroundColor: '#FAFBFC',
    borderRadius: 6,
    border: '1px dashed #E5E7EB',
  },
  ledgerEmptySubtext: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 8,
    fontStyle: 'normal',
    maxWidth: 340,
    margin: '8px auto 0',
  },
  // Recon row
  reconRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #F3F4F6',
    fontSize: 12,
  },
  reconLabel: {
    color: '#6B7280',
  },
  reconValue: {
    color: '#9CA3AF',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
};

// ── Workspace display name helper (matches Team.jsx pattern) ─────────────
function getWorkspaceDisplayName(workspace) {
  return (
    workspace?.label ||
    workspace?.workspaceName ||
    workspace?.name ||
    'This workspace'
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function RevPlanPanel({
  workspace,
  isOpen = false,
  onClose,
  stage,
  mockMode = false,
}) {
  const [activeTab, setActiveTab] = useState('chapters');

  // Highlighted chapter (caller-provided stage takes precedence)
  const activeChapterKey = useMemo(() => {
    if (!stage) return null;
    const match = CHAPTERS.find((c) => c.key === stage.toLowerCase());
    return match?.key || null;
  }, [stage]);

  if (!isOpen && !mockMode) return null;

  const workspaceDisplayName = getWorkspaceDisplayName(workspace);

  const handleBackdropClick = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handlePanelClick = (e) => {
    // Prevent backdrop click bubbling when clicking inside panel
    e.stopPropagation();
  };

  return (
    <>
      {/* Backdrop — closes panel on click (unless mockMode harness) */}
      {!mockMode && (
        <div
          style={styles.backdrop}
          onClick={handleBackdropClick}
          aria-label="Close RevPlan panel"
        />
      )}

      {/* Panel */}
      <aside
        style={mockMode ? { ...styles.panel, position: 'relative', width: '480px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' } : styles.panel}
        onClick={handlePanelClick}
        role="complementary"
        aria-label="RevPlan side panel"
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTopRow}>
            <div style={styles.headerTitleBlock}>
              <div style={styles.badge}>XC-REV · Side Panel</div>
              <h2 style={styles.title}>RevPlan</h2>
              <p style={styles.subtitle}>{workspaceDisplayName}</p>
            </div>
            {!mockMode && (
              <button
                type="button"
                style={styles.closeBtn}
                onClick={onClose}
                aria-label="Close panel"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          <button
            type="button"
            style={{
              ...styles.tab,
              ...(activeTab === 'chapters' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('chapters')}
            aria-pressed={activeTab === 'chapters'}
          >
            Chapters
          </button>
          <button
            type="button"
            style={{
              ...styles.tab,
              ...(activeTab === 'ledger' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('ledger')}
            aria-pressed={activeTab === 'ledger'}
          >
            Ledger
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {activeTab === 'chapters' && (
            <ChaptersTab activeChapterKey={activeChapterKey} />
          )}
          {activeTab === 'ledger' && <LedgerTab />}
        </div>
      </aside>
    </>
  );
}

// ── Chapters tab ─────────────────────────────────────────────────────────
function ChaptersTab({ activeChapterKey }) {
  return (
    <>
      <p style={{
        fontSize: 12,
        color: '#6B7280',
        marginTop: 0,
        marginBottom: 16,
        lineHeight: 1.5,
        fontStyle: 'italic',
      }}>
        RevPlan tracks the property's financial story across its lifecycle.
        Each chapter is populated automatically as discrete processes complete
        their gate events (S13+). Stub view at S7.
      </p>

      {CHAPTERS.map((chapter) => {
        const isActive = chapter.key === activeChapterKey;
        return (
          <div
            key={chapter.key}
            style={
              isActive
                ? { ...styles.chapterCard, ...styles.chapterCardActive }
                : styles.chapterCard
            }
          >
            <div style={styles.chapterHeaderRow}>
              <span style={styles.chapterNumber}>Chapter {chapter.n}</span>
              {isActive && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#003060',
                  backgroundColor: '#00B8D0',
                  padding: '2px 8px',
                  borderRadius: 8,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                }}>
                  Current
                </span>
              )}
            </div>
            <h3 style={styles.chapterTitle}>{chapter.title}</h3>
            <p style={styles.chapterSummary}>{chapter.summary}</p>
            <div style={styles.chapterPlaceholder}>
              Coming in {chapter.arrivesIn}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ── Ledger tab ───────────────────────────────────────────────────────────
function LedgerTab() {
  return (
    <>
      <p style={{
        fontSize: 12,
        color: '#6B7280',
        marginTop: 0,
        marginBottom: 16,
        lineHeight: 1.5,
        fontStyle: 'italic',
      }}>
        Income, expenses, and reconciliation. Entries populate as discrete
        processes fire financial gate events in Tier 3 (Stripe payouts,
        QB sync, vendor work orders, capex draws).
      </p>

      {/* KPI grid placeholders */}
      <div style={styles.ledgerKpiGrid}>
        <div style={styles.ledgerKpiTile}>
          <div style={styles.ledgerKpiLabel}>Income (YTD)</div>
          <div style={styles.ledgerKpiValue}>—</div>
          <div style={styles.ledgerKpiSub}>Tier 3 wiring</div>
        </div>
        <div style={styles.ledgerKpiTile}>
          <div style={styles.ledgerKpiLabel}>Expenses (YTD)</div>
          <div style={styles.ledgerKpiValue}>—</div>
          <div style={styles.ledgerKpiSub}>Tier 3 wiring</div>
        </div>
        <div style={styles.ledgerKpiTile}>
          <div style={styles.ledgerKpiLabel}>Net (YTD)</div>
          <div style={styles.ledgerKpiValue}>—</div>
          <div style={styles.ledgerKpiSub}>Income minus expenses</div>
        </div>
        <div style={styles.ledgerKpiTile}>
          <div style={styles.ledgerKpiLabel}>Reserve Balance</div>
          <div style={styles.ledgerKpiValue}>—</div>
          <div style={styles.ledgerKpiSub}>Capex reserve</div>
        </div>
      </div>

      {/* Transactions card placeholder */}
      <div style={styles.ledgerCard}>
        <div style={styles.ledgerCardHeader}>Recent Transactions</div>
        <div style={styles.ledgerEmptyState}>
          No ledger entries yet.
          <div style={styles.ledgerEmptySubtext}>
            Ledger entries will populate as discrete processes fire gate
            events in Tier 3: Stripe payouts (Operate), QB sync (Maintain),
            vendor invoices (Maintain), capex draws (Develop), sale proceeds
            (Realize).
          </div>
        </div>
      </div>

      {/* Reconciliation card placeholder */}
      <div style={styles.ledgerCard}>
        <div style={styles.ledgerCardHeader}>Expense Reconciliation</div>
        <div style={styles.reconRow}>
          <span style={styles.reconLabel}>QuickBooks linked</span>
          <span style={styles.reconValue}>Tier 3</span>
        </div>
        <div style={styles.reconRow}>
          <span style={styles.reconLabel}>Last sync</span>
          <span style={styles.reconValue}>—</span>
        </div>
        <div style={styles.reconRow}>
          <span style={styles.reconLabel}>Unreconciled count</span>
          <span style={styles.reconValue}>—</span>
        </div>
        <div style={{ ...styles.reconRow, borderBottom: 'none' }}>
          <span style={styles.reconLabel}>Last reconciled</span>
          <span style={styles.reconValue}>—</span>
        </div>
      </div>

      <p style={{
        fontSize: 11,
        color: '#9CA3AF',
        textAlign: 'center',
        margin: '12px 0 0',
        fontStyle: 'italic',
      }}>
        Ledger tab folded into XC-REV per Strategy §3b Row 13 (v1.11 fold-in).
      </p>
    </>
  );
}
